import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { isApiRequest } from '../utils/is-api-request';

/** Chemins d'authentification publics ou auto-gérés : jamais de rafraîchissement automatique sur ces requêtes. */
const NO_REFRESH_SUFFIXES = [
  '/auth/login',
  '/auth/refresh-token',
  '/auth/logout',
  '/auth/register',
  '/auth/verify',
  '/auth/password/forgot',
  '/auth/password/reset',
];

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      const isUnauthorized = error instanceof HttpErrorResponse && error.status === 401;
      if (!isUnauthorized) return throwError(() => error);

      // Un 401 d'un tiers (jamais notre API) n'a aucune raison de déclencher un rafraîchissement de
      // NOTRE token, et surtout jamais de le réattacher en le rejouant vers ce tiers — voir
      // isApiRequest et l'audit sécurité 2026-09-02.
      if (!isApiRequest(req.url)) return throwError(() => error);

      const skipRefresh = NO_REFRESH_SUFFIXES.some((suffix) => req.url.endsWith(suffix));
      if (skipRefresh) {
        void router.navigate(['/auth/login']);
        return throwError(() => error);
      }

      /*
       * Visiteur anonyme : aucun token à rafraîchir, aucune session à clôturer. Un 401 sur une
       * page publique (ex. endpoint de tracking mal sécurisé côté backend, qui doit rester
       * non bloquant pour l'affichage) ne doit jamais arracher un visiteur non connecté de la
       * page qu'il consulte — seul un utilisateur réellement authentifié dont le token a expiré
       * doit passer par le rafraîchissement puis, en cas d'échec, la redirection vers la connexion.
       */
      if (!auth.isAuthenticated()) {
        return throwError(() => error);
      }

      // Le token a peut-être expiré : on tente un rafraîchissement unique avant de rejouer la requête.
      return auth.refreshToken().pipe(
        switchMap((accessToken) =>
          next(req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })),
        ),
        catchError(() => {
          auth.endSessionLocally();
          void router.navigate(['/auth/login']);
          return throwError(() => error);
        }),
      );
    }),
  );
};
